import jsPDF from 'jspdf';
import { formatUserDate, formatUserDateTime } from '@/utils/date-formatter';

export interface TicketHistory {
  id: string;
  ticketId: string;
  userId: string;
  changeType: string;
  fieldName?: string;
  oldValue?: string;
  newValue?: string;
  description?: string;
  createdAt: string;
  userName?: string | null;
}

export interface TicketChatMessage {
  id: string;
  ticketId: string;
  userId: string;
  message: string;
  createdAt: string;
  userName: string;
  userCode: string;
}

export interface ExportOptions {
  includePhotos: boolean;
  includeHistory: boolean;
  includeMessages: boolean;
}

export interface TicketExportData {
  id: string;
  ticketNumber: string;
  title: string;
  status: string;
  prioritaet: string;
  zugewiesenePerson?: string;
  standort?: string;
  beschreibung?: string;
  gewerk?: string;
  typ?: string;
  faelligkeitsdatum?: string;
  geschaetzteKosten?: string;
  fortschritt?: number;
  planLocation?: {
    x: number;
    y: number;
    planId: string;
    zoom: number;
  };
  planName?: string;
  planId?: string;
  weatherData?: string;
  planImageData?: string; // Base64 image data of the plan section
  planImagePinPosition?: { x: number; y: number }; // Pin position within the captured image
  photos?: string[]; // Array of image URLs
  history?: TicketHistory[]; // Array of history entries
  messages?: TicketChatMessage[]; // Array of chat messages
}

export class TicketPDFExportService {
  private pdf: jsPDF;
  private currentY: number = 20;
  private pageHeight: number = 280; // A4 height in mm
  private margin: number = 20;
  private contentWidth: number = 170; // A4 width minus margins
  private dateFormat: string;
  private timeZone: string;

  constructor(options?: { dateFormat?: string; timeZone?: string }) {
    this.pdf = new jsPDF();
    this.dateFormat = options?.dateFormat || 'dd.MM.yyyy';
    this.timeZone = options?.timeZone || 'Europe/Berlin';
  }

  // Helper function to safely convert values to strings
  private safeString(value: any): string {
    if (value === null || value === undefined) {
      return 'Nicht angegeben';
    }
    return String(value);
  }

  exportTickets(tickets: TicketExportData[], projectName: string = 'Project', options?: ExportOptions): void {
    if (tickets.length === 0) {
      throw new Error('No tickets selected for export');
    }

    // Default options: all enabled
    const exportOptions: ExportOptions = options || {
      includePhotos: true,
      includeHistory: true,
      includeMessages: true
    };

    tickets.forEach((ticket, index) => {
      if (index > 0) {
        this.pdf.addPage();
        this.currentY = 20;
      }
      this.addTicketPage(ticket, projectName, exportOptions);
    });

    // Generate filename with timestamp
    const ticketNumber = tickets[0]?.ticketNumber ?? 'ticket';
    const filename = this.buildFileName(projectName, ticketNumber);
    this.pdf.save(filename);
  }

  private addTicketPage(ticket: TicketExportData, projectName: string, options: ExportOptions): void {
    this.currentY = 20;

    // Header
    this.addHeader(ticket, projectName);
    this.currentY += 15;

    // Ticket details section
    this.addTicketDetails(ticket);
    this.currentY += 10;

    // Plan preview section
    this.addPlanPreview(ticket);

    // Photos section (if enabled)
    if (options.includePhotos && ticket.photos && ticket.photos.length > 0) {
      this.addPhotosSection(ticket);
    }

    // History section (if enabled)
    if (options.includeHistory && ticket.history && ticket.history.length > 0) {
      this.addHistorySection(ticket);
    }

    // Messages section (if enabled)
    if (options.includeMessages && ticket.messages && ticket.messages.length > 0) {
      this.addMessagesSection(ticket);
    }
  }

  private addHeader(ticket: TicketExportData, projectName: string): void {
    // Ticket number and project
    this.pdf.setFontSize(16);
    this.pdf.setFont('helvetica', 'bold');
    this.pdf.text(`Ticket #${this.safeString(ticket.ticketNumber)}`, this.margin, this.currentY);
    
    this.pdf.setFontSize(12);
    this.pdf.setFont('helvetica', 'normal');
    this.pdf.text(`${this.safeString(projectName)}`, this.margin + 50, this.currentY);
    
    // Date
    const currentDate = formatUserDate(new Date(), this.dateFormat, this.timeZone);
    this.pdf.text(currentDate, this.contentWidth + this.margin - 30, this.currentY);
    
    this.currentY += 8;
  }

  private addTicketDetails(ticket: TicketExportData): void {
    const details = [
      { label: 'Status', value: this.safeString(ticket.status) },
      { label: 'Titel', value: this.safeString(ticket.title) },
      { label: 'Priorität', value: this.safeString(ticket.prioritaet) },
      { label: 'Zugewiesene Person', value: this.safeString(ticket.zugewiesenePerson) },
      { label: 'Standort', value: this.safeString(ticket.standort) },
      { label: 'Beschreibung', value: this.safeString(ticket.beschreibung) || 'xxxx' },
      { label: 'Gewerk', value: this.safeString(ticket.gewerk) },
      { label: 'Typ', value: this.safeString(ticket.typ) },
      { label: 'Fälligkeitsdatum', value: this.safeString(ticket.faelligkeitsdatum) },
      { label: 'Geschätzte Kosten', value: this.safeString(ticket.geschaetzteKosten) },
      { label: 'Fortschritt', value: `${ticket.fortschritt || 0}%` }
    ];

    this.pdf.setFontSize(10);
    this.pdf.setFont('helvetica', 'normal');

    details.forEach(detail => {
      // Check if we need a new page
      if (this.currentY > this.pageHeight - 20) {
        this.pdf.addPage();
        this.currentY = 20;
      }

      // Label
      this.pdf.setFont('helvetica', 'bold');
      this.pdf.text(`${detail.label}:`, this.margin, this.currentY);
      
      // Value
      this.pdf.setFont('helvetica', 'normal');
      const labelWidth = this.pdf.getTextWidth(`${detail.label}:`);
      this.pdf.text(detail.value, this.margin + labelWidth + 5, this.currentY);
      
      this.currentY += 6;
    });
  }

  private addPlanPreview(ticket: TicketExportData): void {
    // Check if we need a new page
    if (this.currentY > this.pageHeight - 80) {
      this.pdf.addPage();
      this.currentY = 20;
    }

    // Plan preview header
    this.pdf.setFontSize(12);
    this.pdf.setFont('helvetica', 'bold');
    this.pdf.text('Plan-Vorschau', this.margin, this.currentY);
    this.currentY += 8;

    // Plan information
    this.pdf.setFontSize(10);
    this.pdf.setFont('helvetica', 'normal');
    
    const planName = this.safeString(ticket.planName);
    if (planName && planName !== 'Nicht angegeben') {
      this.pdf.text(planName, this.margin, this.currentY);
      this.currentY += 5;
    }
    
    const planId = this.safeString(ticket.planId);
    if (planId && planId !== 'Nicht angegeben') {
      this.pdf.text(`Plan-ID: ${planId}`, this.margin, this.currentY);
      this.currentY += 5;
    }

    // Plan image area
    const imageWidth = 120;
    const imageHeight = 80;
    
    if (ticket.planImageData) {
      try {
        // Add the actual plan image (pin is already rendered in the image)
        this.pdf.addImage(
          ticket.planImageData,
          'JPEG', // or 'PNG' depending on the format
          this.margin,
          this.currentY,
          imageWidth,
          imageHeight
        );
        
        // Pin is now rendered directly in the image, no overlay needed
      } catch (error) {
        console.error('Error adding plan image to PDF:', error);
        // Fallback to visual representation if image fails
        this.addFallbackPlanVisualization(ticket, this.margin, this.currentY, imageWidth, imageHeight);
      }
    } else {
      // Fallback to visual representation if no image data
      this.addFallbackPlanVisualization(ticket, this.margin, this.currentY, imageWidth, imageHeight);
    }

    this.currentY += imageHeight + 10;

    // Position information
    if (ticket.planLocation) {
      this.pdf.setFontSize(10);
      this.pdf.setFont('helvetica', 'normal');
      this.pdf.text(`Position: X=${ticket.planLocation.x}, Y=${ticket.planLocation.y}`, this.margin, this.currentY);
      this.currentY += 5;
    }

    // Pin details
    this.pdf.setFontSize(10);
    this.pdf.setFont('helvetica', 'bold');
    this.pdf.text(`Pin #${this.safeString(ticket.ticketNumber)}`, this.margin, this.currentY);
    this.currentY += 5;
    
    this.pdf.setFont('helvetica', 'normal');
    this.pdf.text(this.safeString(ticket.title), this.margin, this.currentY);
    this.currentY += 5;
    
    this.pdf.text('Zoom: 100%', this.margin, this.currentY);
    this.currentY += 8;

    // Weather data
    this.pdf.setFont('helvetica', 'bold');
    this.pdf.text('Wetterdaten:', this.margin, this.currentY);
    this.currentY += 5;
    
    this.pdf.setFont('helvetica', 'normal');
    this.pdf.text(this.safeString(ticket.weatherData) || 'Keine Wetterdaten verfügbar', this.margin, this.currentY);
  }

  private addFallbackPlanVisualization(ticket: TicketExportData, x: number, y: number, width: number, height: number): void {
    // Outer gray area
    this.pdf.setFillColor(240, 240, 240);
    this.pdf.rect(x, y, width, height, 'F');
    
    // Inner white area (representing zoomed view)
    this.pdf.setFillColor(255, 255, 255);
    this.pdf.rect(x + 10, y + 10, width - 20, height - 20, 'F');
    
    // Plan name in the white area
    this.pdf.setFontSize(8);
    this.pdf.setFont('helvetica', 'normal');
    const displayPlanName = this.safeString(ticket.planName);
    this.pdf.text(displayPlanName !== 'Nicht angegeben' ? displayPlanName : 'Plan', x + 15, y + 20);

    // Pin representation
    if (ticket.planLocation) {
      const pinX = x + 10 + (width - 20) / 2;
      const pinY = y + 10 + (height - 20) / 2;
      
      // Red pin circle
      this.pdf.setFillColor(255, 0, 0);
      this.pdf.circle(pinX, pinY, 3, 'F');
      
      // Pin number
      this.pdf.setFontSize(8);
      this.pdf.setFont('helvetica', 'bold');
      this.pdf.setTextColor(255, 255, 255);
      this.pdf.text(ticket.ticketNumber, pinX - 2, pinY + 1);
      this.pdf.setTextColor(0, 0, 0);
    }
  }

  private addPhotosSection(ticket: TicketExportData): void {
    // Check if we need a new page
    if (this.currentY > this.pageHeight - 60) {
      this.pdf.addPage();
      this.currentY = 20;
    }

    // Photos section header
    this.pdf.setFontSize(12);
    this.pdf.setFont('helvetica', 'bold');
    this.pdf.text('Fotos', this.margin, this.currentY);
    this.currentY += 8;

    if (!ticket.photos || ticket.photos.length === 0) {
      this.pdf.setFontSize(10);
      this.pdf.setFont('helvetica', 'normal');
      this.pdf.text('Keine Fotos verfügbar', this.margin, this.currentY);
      this.currentY += 10;
      return;
    }

    // Add each photo
    ticket.photos.forEach((photoUrl, index) => {
      // Check if we need a new page before adding image
      if (this.currentY > this.pageHeight - 50) {
        this.pdf.addPage();
        this.currentY = 20;
      }

      // Photo label
      this.pdf.setFontSize(10);
      this.pdf.setFont('helvetica', 'bold');
      this.pdf.text(`Foto ${index + 1}:`, this.margin, this.currentY);
      this.currentY += 5;

      // Try to add image (if it's a base64 data URL or valid image URL)
      try {
        // For base64 images
        if (photoUrl.startsWith('data:image/')) {
          const imageFormat = photoUrl.includes('png') ? 'PNG' : 'JPEG';
          const maxImageHeight = 40; // mm
          const maxImageWidth = 100; // mm
          
          // Calculate image dimensions while maintaining aspect ratio
          // We'll use a simple approach: try to fit within max dimensions
          this.pdf.addImage(
            photoUrl,
            imageFormat,
            this.margin,
            this.currentY,
            maxImageWidth,
            maxImageHeight
          );
          this.currentY += maxImageHeight + 5;
        } else {
          // For URL-based images, we can't directly add them to jsPDF
          // Show the URL instead
          this.pdf.setFontSize(9);
          this.pdf.setFont('helvetica', 'normal');
          this.pdf.text(`URL: ${photoUrl.substring(0, 60)}...`, this.margin, this.currentY);
          this.currentY += 5;
        }
      } catch (error) {
        console.error('Error adding photo to PDF:', error);
        this.pdf.setFontSize(9);
        this.pdf.setFont('helvetica', 'normal');
        this.pdf.text('Foto konnte nicht geladen werden', this.margin, this.currentY);
        this.currentY += 5;
      }

      this.currentY += 3;
    });
  }

  private addHistorySection(ticket: TicketExportData): void {
    // Check if we need a new page
    if (this.currentY > this.pageHeight - 60) {
      this.pdf.addPage();
      this.currentY = 20;
    }

    // History section header
    this.pdf.setFontSize(12);
    this.pdf.setFont('helvetica', 'bold');
    this.pdf.text('Historie', this.margin, this.currentY);
    this.currentY += 8;

    if (!ticket.history || ticket.history.length === 0) {
      this.pdf.setFontSize(10);
      this.pdf.setFont('helvetica', 'normal');
      this.pdf.text('Keine Historie verfügbar', this.margin, this.currentY);
      this.currentY += 10;
      return;
    }

    // Add each history entry
    ticket.history.forEach((entry) => {
      // Check if we need a new page
      if (this.currentY > this.pageHeight - 20) {
        this.pdf.addPage();
        this.currentY = 20;
      }

      this.pdf.setFontSize(9);
      this.pdf.setFont('helvetica', 'normal');

      // Date and user
      const date = formatUserDateTime(entry.createdAt, this.dateFormat, this.timeZone);
      const user = entry.userName || 'Unbekannt';
      this.pdf.text(`${date} - ${user}`, this.margin, this.currentY);
      this.currentY += 4;

      // Description
      this.pdf.setFont('helvetica', 'bold');
      const description = entry.description || `${entry.changeType}: ${entry.fieldName || ''}`;
      // Split long descriptions across multiple lines
      const lines = this.pdf.splitTextToSize(description, this.contentWidth);
      this.pdf.text(lines, this.margin, this.currentY);
      this.currentY += lines.length * 4;

      // Old/New values if available
      if (entry.oldValue || entry.newValue) {
        this.pdf.setFont('helvetica', 'normal');
        this.pdf.setFontSize(8);
        if (entry.oldValue) {
          this.pdf.text(`Alt: ${this.safeString(entry.oldValue)}`, this.margin + 5, this.currentY);
          this.currentY += 4;
        }
        if (entry.newValue) {
          this.pdf.text(`Neu: ${this.safeString(entry.newValue)}`, this.margin + 5, this.currentY);
          this.currentY += 4;
        }
      }

      this.currentY += 3;
    });
  }

  private addMessagesSection(ticket: TicketExportData): void {
    // Check if we need a new page
    if (this.currentY > this.pageHeight - 60) {
      this.pdf.addPage();
      this.currentY = 20;
    }

    // Messages section header
    this.pdf.setFontSize(12);
    this.pdf.setFont('helvetica', 'bold');
    this.pdf.text('Nachrichten/Chat', this.margin, this.currentY);
    this.currentY += 8;

    if (!ticket.messages || ticket.messages.length === 0) {
      this.pdf.setFontSize(10);
      this.pdf.setFont('helvetica', 'normal');
      this.pdf.text('Keine Nachrichten verfügbar', this.margin, this.currentY);
      this.currentY += 10;
      return;
    }

    // Add each message
    ticket.messages.forEach((message) => {
      // Check if we need a new page
      if (this.currentY > this.pageHeight - 20) {
        this.pdf.addPage();
        this.currentY = 20;
      }

      this.pdf.setFontSize(9);
      this.pdf.setFont('helvetica', 'normal');

      // Date and user
      const date = formatUserDateTime(message.createdAt, this.dateFormat, this.timeZone);
      const user = message.userName || message.userCode || 'Unbekannt';
      this.pdf.text(`${date} - ${user}`, this.margin, this.currentY);
      this.currentY += 4;

      // Message content
      this.pdf.setFont('helvetica', 'normal');
      // Split long messages across multiple lines
      const lines = this.pdf.splitTextToSize(message.message, this.contentWidth);
      this.pdf.text(lines, this.margin + 5, this.currentY);
      this.currentY += lines.length * 4 + 3;
    });
  }

  private buildFileName(projectName: string, ticketNumber: string): string {
    const safeProject = this.sanitizeForFilename(projectName, 'project');
    const safeTicket = this.sanitizeForFilename(ticketNumber, 'ticket');
    const timestamp = this.formatShortTimestamp();
    return `${safeProject}_${safeTicket}_${timestamp}.pdf`;
  }

  private formatShortTimestamp(date: Date = new Date()): string {
    const pad = (value: number) => String(value).padStart(2, '0');
    const year = date.getFullYear();
    const month = pad(date.getMonth() + 1);
    const day = pad(date.getDate());
    const hours = pad(date.getHours());
    const minutes = pad(date.getMinutes());
    return `${year}${month}${day}_${hours}${minutes}`;
  }

  private sanitizeForFilename(value: string | undefined, fallback: string): string {
    if (!value) return fallback;
    const cleaned = value
      .trim()
      .replace(/\s+/g, '_')
      .replace(/[^A-Za-z0-9_-]/g, '')
      .replace(/_+/g, '_')
      .replace(/^_+|_+$/g, '');
    return cleaned || fallback;
  }
}
